import { Session as Family, type SessionAnswer, type SessionOptions, type SessionState } from '@beyond-js/ui';
import { ApiProblem, type Api } from '@cdn/api';
import { Resource } from '@cdn/state';
import { Outages, type Cause } from '@cdn/ui';
import type { Identity } from './port';
import type { Session } from './session';

/** The `Session` options a deployment or a test may change: whether it renews silently, and its bounds */
export type Pace = Partial<Pick<SessionOptions, 'silent' | 'pause' | 'bound' | 'wait' | 'interval'>>;

interface Specs {
	session: Session;
	api: Api;
	identity: Identity;
	/** The address in view, absolute */
	here: () => string;
	/** Beyond Accounts' account page, offered to a suspended account */
	accounts?: string;
	/** Accounts' `/leave` with the way back to an address, when this deployment knows Accounts */
	leave: (returning: string) => string | undefined;
	/** The language the person reads the application in */
	language: () => string;
	/** Sends the browser to an address; tests observe it instead */
	navigate: (url: string) => void;
	pace?: Pace;
	document?: Document;
	/** Runs once the session was renewed: the live transports reconnect */
	onrenewed?: () => void;
}

/**
 * The family's answer to a session that ends while the person works (Beyond Suite's `docs/family/session-renewal.md`):
 * one `Session` of `@beyond-js/ui` for the page, created once CDN's session says who is signed in and ended when
 * they sign out. Every request whose session ended waits on it (`api.lapse`): it renews the session in a hidden
 * frame through the service's hand-off (`mode=silent`), or asks the person once in the family's dialog, whose
 * sign-in runs in a window; the screens stay where they were. The dialog cannot be dismissed (D63 as amended on
 * 2026-10-07): there is no state in which a page of CDN is open without a session, so what a page asks for
 * meanwhile waits behind it. It never shows CDN's own dialog, banner or "Sign in".
 *
 * - `read()` is CDN's session document (`session.read`), read without waiting on itself: the person on `signed`,
 *   Accounts' reason on `ended`, and `unavailable` for an outage, which is never a sign-out, naming who did not
 *   answer as the page's lines do (Beyond CDN, or Beyond Accounts when the service says so; ui 0.33.0's `source`).
 * - "Use another account" is "Sign out of Beyond" (Q09); a suspended account is offered Accounts' account page.
 * - Renewed: the session document is read again, every view whose read was not sent again is read again
 *   (`Resource.resume()`) and the live transports reconnect (`onrenewed`). Someone else signed in: the previous
 *   person's drafts are dropped and the application starts again at its entry.
 * - A live transport that dropped asks `check()` before it says anything of its own.
 */
export class Renewal {
	/** Who did not answer CDN's session document, by the ids the family `Session` names (`CauseName`) */
	static #sources: Record<Cause, string> = { accounts: 'accounts', projects: 'projects', snapshots: 'snapshots', cdn: 'cdn', upstream: 'cdn' };

	#specs: Specs;
	#family: Family | undefined;
	#release: (() => void) | undefined;
	#stop: () => void;

	constructor(specs: Specs) {
		this.#specs = specs;
		this.#stop = specs.session.subscribe(() => this.#follow());
		this.#follow();
	}

	/**
	 * Looks at the session now, for a live transport that dropped: it says nothing of its own unless this resolves
	 * `signed`. Without a `Session` (nobody signed in) there is no dialog to speak instead, so it is `signed`
	 */
	check(): Promise<SessionState> {
		return this.#family?.check() ?? Promise.resolve('signed');
	}

	/** The page's `Session`, while someone is signed in */
	get current() {
		return this.#family;
	}

	destroy() {
		this.#stop();
		this.#end();
	}

	// One `Session` while CDN's session is active, none otherwise
	#follow() {
		const active = this.#specs.session.status === 'active';
		if (active && !this.#family) this.#begin();
		else if (!active && this.#family) this.#end();
	}

	#begin() {
		const { session, api, identity, here, accounts, leave, language, navigate, pace, document, onrenewed } = this.#specs;
		const user = session.user;
		const origin = new URL(here()).origin;
		this.#family = new Family({
			product: 'cdn',
			person: user ? { id: user.id, name: user.name ?? null, email: user.email ?? null } : null,
			read: () => this.#read(),
			// Without a hand-off of its own (the simulated sign-in), signing in again is this application's sign-in screen
			start: mode => identity.start?.(mode, here()) ?? (mode === 'tab' ? `${origin}/signin?return=${encodeURIComponent(new URL(here()).pathname)}` : null),
			other: () => void session.leave(leave(here())),
			accounts: accounts ?? null,
			onrenewed: () => {
				void session.refresh().catch(() => undefined);
				Resource.resume();
				onrenewed?.();
			},
			onchanged: () => {
				session.drafts.drop();
				navigate(`${origin}/`);
			},
			labels: Family.labels[language() === 'es' ? 'es' : 'en'],
			...(document ? { document } : {}),
			...pace
		});
		this.#release = api.lapse.attach(this.#family);
	}

	#end() {
		this.#release?.();
		this.#release = undefined;
		this.#family?.destroy();
		this.#family = undefined;
	}

	async #read(): Promise<SessionAnswer> {
		try {
			const { user } = await this.#specs.api.call('session.read', { replay: null });
			return { state: 'signed', person: { id: user.id, name: user.name ?? null, email: user.email ?? null } };
		} catch (thrown) {
			const problem = ApiProblem.of(thrown);
			if (problem.ended) return { state: 'ended', reason: problem.reason ?? null };
			// Who did not answer, as the page's lines name it: Beyond Accounts only when the service says so
			if (problem.outage) return { state: 'unavailable', source: Renewal.#sources[Outages.who(problem)] };
			throw problem;
		}
	}
}
